import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Truck, ThumbsUp } from 'lucide-react';

const stats = [
  { icon: MapPin, value: "Toute la Suisse", label: "Zone couverte", isText: true },
  { icon: Truck, value: 500, suffix: "+", label: "Déménagements et transports réalisés", isText: false },
  { icon: ThumbsUp, value: 96, suffix: "%", label: "Satisfaction clients", isText: false },
];

function AnimatedCounter({ target, suffix, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return (
    <span ref={ref} className="font-['Oswald'] text-4xl sm:text-5xl font-bold text-white">
      {count}{suffix}
    </span>
  );
}

export default function KeyFigures() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="relative py-20 md:py-24 bg-[#FF0000] overflow-hidden" data-testid="key-figures-section">
      {/* Diagonal accent */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute -top-1/2 -left-1/4 w-[80%] h-[200%] bg-black/20 rotate-12" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-12 max-w-4xl mx-auto">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="text-center"
              data-testid={`stat-${index}`}
            >
              <stat.icon className="w-8 h-8 text-white/80 mx-auto mb-4" />
              {stat.isText ? (
                <span className="font-['Oswald'] text-3xl sm:text-4xl font-bold text-white block">
                  {stat.value}
                </span>
              ) : (
                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
              )}
              <p className="text-white/80 mt-2 text-sm uppercase tracking-wider font-medium">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
